import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { adminHeaders } from '../../lib/adminAuth';
import { useAdminI18n } from '../../lib/adminI18n';
import './DashboardPage.css';
import './OtaReviewPage.css';

interface PropertyStatus {
  property_id: string;
  property_name: string;
  label: string;
  checkin: boolean;
  checkout: boolean;
  staying: boolean;
  vacant: boolean;
}

interface UpcomingRow {
  id: string;
  property_name: string;
  booking_prefix?: string | null;
  checkin_date: string;
  checkout_date: string;
  guest_count: number;
  passport_received: number;
  checkin_status: string;
  guest_name: string;
}

interface NextMonthAvailability {
  property_id: string;
  property_name: string;
  label: string;
  booking_prefix?: string | null;
  total_days: number;
  vacant_days: number;
}

interface DashboardData {
  today_checkins: any[];
  pending_transfers: any[];
  paid_expired?: any[];
  recent_reservations_count: number;
  total_reservations: number;
  new_week_reservations: number;
  pending_approval_count: number;
  properties_status: PropertyStatus[];
  upcoming: UpcomingRow[];
  next_month_label: string;
  next_month_availability: NextMonthAvailability[];
}

function DashboardPage() {
  const { t } = useTranslation();
  const { at } = useAdminI18n();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  // 清掃の件数（清掃管理のダッシュボードAPIから取得。失敗してもこの画面は表示する）
  const [cleaning, setCleaning] = useState<{ today: number; today_done: number; tomorrow: number; unassigned_week: number } | null>(null);
  const [cleaningErr, setCleaningErr] = useState(false);
  // OTA同期の確認件数（失敗しても画面は表示する）
  const [ota, setOta] = useState<{ incomplete?: number; review?: number; pending?: number; date_changed?: number; blocks?: number } | null>(null);
  useEffect(() => {
    fetch('/api/admin/ota-review/summary', { headers: adminHeaders(), credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setOta).catch(() => {});
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchCleaning();
  }, []);

  const fetchCleaning = async () => {
    try {
      const res = await fetch('/api/admin/cleaning/dashboard', { headers: adminHeaders(), credentials: 'include' });
      if (!res.ok) throw new Error(String(res.status));
      const d = await res.json();
      setCleaning(d?.counts || null);
    } catch (err) {
      console.error('Cleaning summary error:', err);
      setCleaningErr(true);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await fetch('/api/admin/dashboard', { headers: adminHeaders() });
      if (!response.ok) throw new Error('Failed to fetch dashboard');
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  // チェックイン状態のラベル（checkin_status: not_started / in_progress / completed など）
  const checkinStatusBadge = (status: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      not_started: { label: at('db.csNone'), cls: 'cs-none' },
      in_progress: { label: at('db.csProgress'), cls: 'cs-progress' },
      completed: { label: at('db.csDone'), cls: 'cs-done' },
      checked_in: { label: at('db.csDone'), cls: 'cs-done' },
    };
    const v = map[status] || { label: status || at('db.csNone'), cls: 'cs-none' };
    return <span className={`ds-badge ${v.cls}`}>{v.label}</span>;
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="dashboard">
      <h1>{t('admin.dashboard')}</h1>

      {/* 要対応（期限切れ後に決済）: 1件以上あるときだけ目立つ警告を表示 */}
      {(data?.paid_expired?.length || 0) > 0 && (
        <div className="attention-banner">
          <div className="attention-head">
            <strong>{at('db.attention', { n: data!.paid_expired!.length })}</strong>
            <span>{at('db.attentionText')}</span>
            <button type="button" className="attention-link"
              onClick={() => navigate('/admin/reservations?status=PAID_EXPIRED')}>
              {at('db.viewList')}
            </button>
          </div>
        </div>
      )}

      {/* ===== 1段目: 予約件数の4カード（総予約・新規・承認待ち・OTA同期の確認） ===== */}
      <section className="ds-section">
        <div className="ds-stats ds-stats-4">
          <button type="button" className="ds-stat clickable" onClick={() => navigate('/admin/reservations')}>
            <h3>{at('db.total')}</h3>
            <div className="ds-stat-value">{data?.total_reservations ?? 0}</div>
            <span className="ds-stat-link">{at('db.viewList')}</span>
          </button>
          <button type="button" className="ds-stat clickable" onClick={() => navigate('/admin/reservations')}>
            <h3>{at('db.newWeek')}</h3>
            <div className="ds-stat-value">{data?.new_week_reservations ?? 0}</div>
            <span className="ds-stat-sub">{at('db.newWeekSub')}</span>
          </button>
          <button type="button" className="ds-stat clickable warning" onClick={() => navigate('/admin/reservations?status=PENDING_TRANSFER')}>
            <h3>{at('db.pending')}</h3>
            <div className="ds-stat-value">{data?.pending_approval_count ?? 0}</div>
            <span className="ds-stat-link">{at('db.viewList')}</span>
          </button>
          {(() => {
            // OTA同期の確認: 件数の合計。内訳は件数のあるものだけ表示し、クリックで該当タブへ
            const o = ota || {};
            const parts = [
              { tab: 'review', label: 'キャンセル疑い・重複', n: o.review || 0 },
              { tab: 'blocks', label: 'ブロック確認', n: o.blocks || 0 },
              { tab: 'pending', label: 'UID変更の疑い', n: o.pending || 0 },
              { tab: 'dates', label: '日程変更', n: o.date_changed || 0 },
              { tab: 'incomplete', label: '予約情報補完待ち', n: o.incomplete || 0 },
            ];
            const total = parts.reduce((x, p) => x + p.n, 0);
            const first = parts.find((p) => p.n > 0);
            return (
              <button type="button" className={`ds-stat clickable ${total > 0 ? 'ota-alert' : ''}`}
                onClick={() => navigate(`/admin/calendar?view=ota${first ? `&tab=${first.tab}` : ''}`)}>
                <h3>{at('nav.otaReview')}</h3>
                <div className="ds-stat-value">{ota ? total : '-'}</div>
                {total > 0 ? (
                  <span className="ds-ota-parts">
                    {parts.filter((p) => p.n > 0).map((p) => (
                      <span key={p.tab} className="ds-ota-part" role="link"
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/calendar?view=ota&tab=${p.tab}`); }}>
                        {p.label} {p.n}
                      </span>
                    ))}
                  </span>
                ) : <span className="ds-stat-link">{at('db.viewList')}</span>}
              </button>
            );
          })()}
        </div>
      </section>

      {/* ===== 2段目: 3物件の来月の空き日数 ===== */}
      <section className="ds-section">
        <h2 className="ds-section-title">{at('db.availTitle')}</h2>
        <div className="ds-avail-grid">
          {(data?.next_month_availability || []).map((a) => (
            <button key={a.property_id} type="button" className="ds-avail-row clickable"
              onClick={() => navigate('/admin/calendar')} title={at('db.availTip', { name: a.property_name, v: a.vacant_days, t: a.total_days })}>
              <span className="ds-avail-name">{a.property_name}</span>
              <span className="ds-avail-num"><strong>{a.vacant_days}</strong><span className="ds-avail-unit"> / {a.total_days}{at('db.daysUnit')}</span></span>
            </button>
          ))}
          {(data?.next_month_availability?.length || 0) === 0 && (
            <p className="no-data">{at('db.noProps')}</p>
          )}
        </div>
      </section>

      {/* ===== 2段目: Today's Status（物件ごと） ===== */}
      <section className="ds-section">
        <h2 className="ds-section-title">Today's Status</h2>
        <div className="ds-prop-grid">
          {(data?.properties_status || []).map((p) => (
            <div key={p.property_id} className={`ds-prop-card ${p.vacant ? 'is-vacant' : ''}`} title={p.property_name}>
              <div className="ds-prop-name">{p.property_name}</div>
              <div className="ds-prop-badges">
                {p.checkin && <span className="ds-badge st-checkin">{at('db.checkin')}</span>}
                {p.checkout && <span className="ds-badge st-checkout">{at('db.checkout')}</span>}
                {p.staying && <span className="ds-badge st-staying">{at('db.staying')}</span>}
                {p.vacant && <span className="ds-badge st-vacant">{at('db.vacant')}</span>}
              </div>
            </div>
          ))}
          {(data?.properties_status?.length || 0) === 0 && (
            <p className="no-data">{at('db.noProps')}</p>
          )}
        </div>
      </section>

      {/* ===== 2.5段目: 清掃（件数） ===== */}
      <section className="ds-section">
        <h2 className="ds-section-title">{at('db.cleaning')}</h2>
        {cleaningErr ? (
          <p className="no-data">{at('db.cleaningErr')}</p>
        ) : (
          <div className="ds-stats ds-cleaning">
            <div className="ds-stat clickable" role="button" tabIndex={0}
              onClick={() => navigate('/admin/cleaning')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/admin/cleaning'); }}>
              <h3>{at('db.cleanToday')}</h3>
              <div className="ds-stat-value">{cleaning ? cleaning.today : '—'}</div>
              <span className="ds-stat-sub">{cleaning ? at('db.cleanTodaySub', { d: cleaning.today_done, r: cleaning.today - cleaning.today_done }) : '\u00a0'}</span>
            </div>
            <div className="ds-stat clickable" role="button" tabIndex={0}
              onClick={() => navigate('/admin/cleaning')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/admin/cleaning'); }}>
              <h3>{at('db.cleanTomorrow')}</h3>
              <div className="ds-stat-value">{cleaning ? cleaning.tomorrow : '—'}</div>
              <span className="ds-stat-link">{at('db.openCleaning')}</span>
            </div>
            <div className={`ds-stat clickable ${cleaning && cleaning.unassigned_week > 0 ? 'warning' : ''}`} role="button" tabIndex={0}
              onClick={() => navigate('/admin/cleaning')} onKeyDown={(e) => { if (e.key === 'Enter') navigate('/admin/cleaning'); }}>
              <h3>{at('db.unassigned')}</h3>
              <div className="ds-stat-value">{cleaning ? cleaning.unassigned_week : '—'}</div>
              <span className="ds-stat-sub">{at('db.unassignedSub')}</span>
            </div>
          </div>
        )}
      </section>

      {/* ===== 3段目: 1週間以内の Checkin / Checkout 一覧 ===== */}
      <section className="ds-section">
        <h2 className="ds-section-title">{at('db.upcoming')}</h2>
        {(data?.upcoming?.length || 0) === 0 ? (
          <p className="no-data">{at('db.noUpcoming')}</p>
        ) : (
          <div className="ds-table-wrap">
            <table className="ds-table">
              <thead>
                <tr>
                  <th>{at('db.colProp')}</th>
                  <th>{at('db.checkin')}</th>
                  <th>{at('db.checkout')}</th>
                  <th>{at('db.colGuests')}</th>
                  <th>{at('db.colPassport')}</th>
                  <th>{at('db.colCheckinSt')}</th>
                </tr>
              </thead>
              <tbody>
                {data!.upcoming.map((r) => (
                  <tr key={r.id} className="clickable-row" onClick={() => navigate(`/admin/reservations?highlight=${r.id}`)}>
                    <td data-label={at('db.colProp')}>{r.property_name}</td>
                    <td data-label={at('db.checkin')}>{r.checkin_date}</td>
                    <td data-label={at('db.checkout')}>{r.checkout_date}</td>
                    <td data-label={at('db.colGuests')}>{at('db.guestsN', { n: r.guest_count })}</td>
                    <td data-label={at('db.colPassport')}>
                      {r.passport_received
                        ? <span className="ds-badge cs-done">{at('db.registered')}</span>
                        : <span className="ds-badge cs-none">{at('db.notRegistered')}</span>}
                    </td>
                    <td data-label={at('db.colCheckinSt')}>{checkinStatusBadge(r.checkin_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

export default DashboardPage;
